/**
 * Templates Page
 * 
 * Epic 14: Full template lifecycle - create, edit, select, test.
 * Per design.md: Premium UI with semantic tokens, lucide icons, no emojis.
 * 
 * Two tabs: My Templates | Starter Library
 */

import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from 'convex/_generated/api';
import type { Doc } from 'convex/_generated/dataModel';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Card, CardContent,
    Button, Badge, Input, Skeleton,
    Plus, Search, FileText, Trash2, Edit, Library, Zap,
    Dialog, DialogContent,
} from '@uprosal/ui';
import { TemplateDialog } from './TemplateDialog';
import { StarterTemplateCard } from './StarterTemplateCard';
import { PromptPlayground } from './PromptPlayground';
import { ConfirmDialog } from '../../components/ConfirmDialog';

type TabValue = 'my-templates' | 'starter-library';

export function TemplatesPage() {
    const [activeTab, setActiveTab] = useState<TabValue>('my-templates');
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<Doc<'prompts'> | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<Doc<'prompts'> | null>(null);
    const [playgroundTemplate, setPlaygroundTemplate] = useState<Doc<'prompts'> | null>(null);

    // Fetch user templates and starters from Convex
    const userTemplates = useQuery(api.queries.prompts.list, {});
    const starterTemplates = useQuery(api.queries.prompts.listStarters, {});
    const archiveTemplate = useMutation(api.mutations.prompts.archive);
    const cloneTemplate = useMutation(api.mutations.prompts.clone);

    // Filter templates by search query
    const filteredUserTemplates = userTemplates?.filter((t: Doc<'prompts'>) =>
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.description?.toLowerCase().includes(searchQuery.toLowerCase())) ||
        t.template.toLowerCase().includes(searchQuery.toLowerCase())
    ) ?? [];

    const filteredStarters = starterTemplates?.filter((t: Doc<'prompts'>) =>
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.description?.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (t.category?.toLowerCase().includes(searchQuery.toLowerCase()))
    ) ?? [];

    const handleNewTemplate = () => {
        setEditingTemplate(null);
        setDialogOpen(true);
    };

    const handleEditTemplate = (template: Doc<'prompts'>) => {
        setEditingTemplate(template);
        setDialogOpen(true);
    };

    const handleArchiveTemplate = (template: Doc<'prompts'>) => {
        setDeleteConfirm(template);
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        try {
            await archiveTemplate({ promptId: deleteConfirm._id });
        } catch (error) {
            console.error('Failed to delete template:', error);
        }
        setDeleteConfirm(null);
    };

    const handleCloneStarter = async (starterId: Doc<'prompts'>['_id']) => {
        try {
            const newId = await cloneTemplate({ starterId });
            // Switch to My Templates tab to show the new clone
            setActiveTab('my-templates');
            console.log('Cloned template:', newId);
        } catch (error) {
            console.error('Failed to clone template:', error);
        }
    };

    const extractVariables = (template: string): string[] => {
        const regex = /\{\{(\w+)\}\}/g;
        const matches = new Set<string>();
        let match;
        while ((match = regex.exec(template)) !== null) {
            matches.add(match[1]);
        }
        return Array.from(matches);
    };

    const isLoading = activeTab === 'my-templates'
        ? userTemplates === undefined
        : starterTemplates === undefined;

    const currentItems = activeTab === 'my-templates'
        ? filteredUserTemplates
        : filteredStarters;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Templates</h1>
                    <p className="text-muted-foreground mt-1">
                        Manage your proposal templates or browse starter templates
                    </p>
                </div>
                <Button className="flex items-center gap-2" onClick={handleNewTemplate}>
                    <Plus className="w-4 h-4" />
                    New Template
                </Button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-border">
                <button
                    onClick={() => setActiveTab('my-templates')}
                    className={`px-4 py-2 text-sm font-medium transition-colors relative ${activeTab === 'my-templates'
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                        }`}
                >
                    <span className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        My Templates
                        {userTemplates && (
                            <Badge variant="secondary" className="text-xs">
                                {userTemplates.length}
                            </Badge>
                        )}
                    </span>
                    {activeTab === 'my-templates' && (
                        <motion.div
                            layoutId="activeTab"
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                        />
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('starter-library')}
                    className={`px-4 py-2 text-sm font-medium transition-colors relative ${activeTab === 'starter-library'
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                        }`}
                >
                    <span className="flex items-center gap-2">
                        <Library className="w-4 h-4" />
                        Starter Library
                        {starterTemplates && (
                            <Badge variant="secondary" className="text-xs">
                                {starterTemplates.length}
                            </Badge>
                        )}
                    </span>
                    {activeTab === 'starter-library' && (
                        <motion.div
                            layoutId="activeTab"
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                        />
                    )}
                </button>
            </div>

            {/* Search */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        type="text"
                        placeholder={activeTab === 'my-templates'
                            ? "Search your templates..."
                            : "Search starter templates..."
                        }
                        className="w-full pl-10"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            {/* Loading state */}
            {isLoading && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Card key={i}>
                            <CardContent className="pt-6">
                                <div className="flex items-start justify-between mb-3">
                                    <Skeleton className="w-10 h-10 rounded-lg" />
                                    <Skeleton className="w-6 h-6" />
                                </div>
                                <Skeleton className="h-5 w-32 mb-2" />
                                <Skeleton className="h-4 w-full mb-1" />
                                <Skeleton className="h-4 w-2/3 mb-3" />
                                <div className="flex gap-2">
                                    <Skeleton className="h-5 w-16" />
                                    <Skeleton className="h-5 w-16" />
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Empty state */}
            {!isLoading && currentItems.length === 0 && (
                <Card className="py-12">
                    <CardContent className="text-center">
                        <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                            {activeTab === 'my-templates' ? (
                                <FileText className="w-8 h-8 text-primary" />
                            ) : (
                                <Library className="w-8 h-8 text-primary" />
                            )}
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                            {searchQuery
                                ? 'No templates found'
                                : activeTab === 'my-templates'
                                    ? 'No templates yet'
                                    : 'No starter templates available'
                            }
                        </h3>
                        <p className="text-muted-foreground mb-4">
                            {searchQuery
                                ? 'Try a different search term.'
                                : activeTab === 'my-templates'
                                    ? 'Create your first template or browse the Starter Library.'
                                    : 'Starter templates will appear here once seeded.'
                            }
                        </p>
                        {!searchQuery && activeTab === 'my-templates' && (
                            <div className="flex gap-3 justify-center">
                                <Button onClick={handleNewTemplate}>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Create Template
                                </Button>
                                <Button variant="outline" onClick={() => setActiveTab('starter-library')}>
                                    <Library className="w-4 h-4 mr-2" />
                                    Browse Starters
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* My Templates Grid */}
            <AnimatePresence mode="wait">
                {!isLoading && activeTab === 'my-templates' && filteredUserTemplates.length > 0 && (
                    <motion.div
                        key="my-templates"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                    >
                        {filteredUserTemplates.map((template: Doc<'prompts'>, i: number) => {
                            const variables = extractVariables(template.template);

                            return (
                                <motion.div
                                    key={template._id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: i * 0.05 }}
                                    className="h-full"
                                >
                                    <Card className="group cursor-pointer hover:shadow-lg transition-shadow h-full flex flex-col">
                                        <CardContent className="pt-6 flex flex-col flex-1">
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="p-2 rounded-lg bg-primary/10">
                                                    <FileText className="w-5 h-5 text-primary" />
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                                                        title="Edit template"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditTemplate(template);
                                                        }}
                                                    >
                                                        <Edit className="w-4 h-4 text-muted-foreground" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                                                        title="Test template"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setPlaygroundTemplate(template);
                                                        }}
                                                    >
                                                        <Zap className="w-4 h-4 text-muted-foreground" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                                                        title="Delete template"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleArchiveTemplate(template);
                                                        }}
                                                    >
                                                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                                                    </Button>
                                                </div>
                                            </div>

                                            <h3
                                                className="font-semibold text-foreground mb-1 truncate max-w-full"
                                                onClick={() => handleEditTemplate(template)}
                                                title={template.name}
                                            >
                                                {template.name}
                                            </h3>

                                            {/* Description - fixed height with line clamp */}
                                            <p className="text-sm text-muted-foreground line-clamp-1 mb-2 min-h-[1.25rem]">
                                                {template.description || '\u00A0'}
                                            </p>

                                            {/* Template preview - fixed height */}
                                            <p className="text-xs text-muted-foreground line-clamp-2 mb-3 font-mono bg-muted/50 p-2 rounded min-h-[3.5rem]">
                                                {template.template.substring(0, 80)}
                                                {template.template.length > 80 && '...'}
                                            </p>

                                            {/* Category & Variables - fixed min height */}
                                            <div className="flex flex-wrap gap-1 mb-3 min-h-[1.5rem]">
                                                {template.category && (
                                                    <Badge variant="outline" className="text-xs">
                                                        {template.category}
                                                    </Badge>
                                                )}
                                                {variables.slice(0, 2).map((v) => (
                                                    <Badge key={v} variant="secondary" className="text-xs">
                                                        {v}
                                                    </Badge>
                                                ))}
                                                {variables.length > 2 && (
                                                    <Badge variant="secondary" className="text-xs">
                                                        +{variables.length - 2}
                                                    </Badge>
                                                )}
                                            </div>

                                            {/* Footer - pushed to bottom */}
                                            <div className="flex items-center justify-between text-xs mt-auto">
                                                <span className="text-muted-foreground">
                                                    {new Date(template.createdAt).toLocaleDateString()}
                                                </span>
                                                <Badge variant="success">Active</Badge>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                )}

                {/* Starter Library Grid */}
                {!isLoading && activeTab === 'starter-library' && filteredStarters.length > 0 && (
                    <motion.div
                        key="starter-library"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                    >
                        {filteredStarters.map((template: Doc<'prompts'>, i: number) => (
                            <StarterTemplateCard
                                key={template._id}
                                template={template}
                                index={i}
                                onUseTemplate={() => handleCloneStarter(template._id)}
                            />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Create/Edit Dialog */}
            <TemplateDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                template={editingTemplate}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={!!deleteConfirm}
                onOpenChange={(open) => !open && setDeleteConfirm(null)}
                title="Delete Template"
                description={
                    <>
                        Are you sure you want to delete "{deleteConfirm?.name && deleteConfirm.name.length > 30 ? deleteConfirm.name.slice(0, 30) + '...' : deleteConfirm?.name}"?
                        <br /><br />
                        This action cannot be undone.
                    </>
                }
                confirmLabel="Delete"
                cancelLabel="Cancel"
                variant="destructive"
                onConfirm={confirmDelete}
            />

            {/* Playground Dialog */}
            <Dialog
                open={!!playgroundTemplate}
                onOpenChange={(open) => !open && setPlaygroundTemplate(null)}
            >
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    {playgroundTemplate && (
                        <PromptPlayground
                            template={playgroundTemplate}
                            onClose={() => setPlaygroundTemplate(null)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
